<template>
  <transition name="subitems-overlay">
    <div
      v-if="visible"
      v-click-outside="handleClickOutside"
      class="subitems-overlay"
      :style="positionStyle"
      @click.stop
    >
      <!-- Connection line to parent icon -->
      <div class="subitems-connector" />

      <!-- Header -->
      <div class="subitems-header">
        <span class="subitems-title">{{ parentLabel }}</span>
        <button class="subitems-toggle-all" @click="toggleAll">
          {{ allActive ? 'None' : 'All' }}
        </button>
      </div>

      <!-- Sub-items list -->
      <div class="subitems-list">
        <button
          v-for="item in items"
          :key="item.id"
          class="subitem-row"
          :class="{ 'is-active': isActive(item) }"
          :style="getItemStyle(item)"
          @click="toggle(item)"
        >
          <!-- Color indicator pip -->
          <div class="subitem-pip" :style="{ '--item-color': item.color }" />

          <!-- Icon (if provided) -->
          <v-icon v-if="item.icon" size="16" class="subitem-icon">
            {{ item.icon }}
          </v-icon>

          <!-- Label -->
          <span class="subitem-label">{{ item.label }}</span>

          <!-- Active check mark -->
          <div class="subitem-check">
            <v-icon v-if="isActive(item)" size="14">mdi-check</v-icon>
          </div>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TagSubItemsOverlay',

  props: {
    visible: { type: Boolean, default: false },
    parentLabel: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    position: { type: Object, default: () => ({ x: 0, y: 0 }) },
    displaySettings: { type: Object, default: null },
  },

  computed: {
    positionStyle() {
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
      };
    },

    allActive() {
      return this.items.every(item => this.displaySettings?.[item.flag]);
    },
  },

  methods: {
    isActive(item) {
      return this.displaySettings?.[item.flag];
    },

    getItemStyle(item) {
      return {
        '--item-color': item.color,
        '--item-color-rgb': this.hexToRgb(item.color),
      };
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '120, 156, 74';
    },

    toggle(item) {
      this.$emit('toggle', item);
    },

    toggleAll() {
      this.$emit('toggle-all', !this.allActive);
    },

    handleClickOutside() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================================================
// Tag Sub-Items Overlay - Industrial Control Panel Style
// ============================================================================

.subitems-overlay {
  position: absolute;
  transform: translateX(-50%);
  z-index: 1001;
  min-width: 180px;
  border-radius: var(--ds-radius-md);
  backdrop-filter: blur(16px);
  overflow: hidden;

  // Dark theme (default)
  background: linear-gradient(
    180deg,
    rgba(25, 30, 35, 0.98) 0%,
    rgba(18, 22, 26, 0.98) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);

  // Light theme
  .theme--light & {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(248, 250, 252, 0.98) 100%
    );
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.15),
      0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

// ============================================================================
// Connector Line
// ============================================================================
.subitems-connector {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.2) 100%);

  .theme--light & {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.15) 100%);
  }
}

// ============================================================================
// Header
// ============================================================================
.subitems-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  .theme--light & {
    border-bottom-color: rgba(0, 0, 0, 0.06);
  }
}

.subitems-title {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);

  .theme--light & {
    color: rgba(0, 0, 0, 0.45);
  }
}

.subitems-toggle-all {
  font-size: 9px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.8);
  }

  .theme--light & {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      color: rgba(0, 0, 0, 0.8);
    }
  }
}

// ============================================================================
// Sub-items List
// ============================================================================
.subitems-list {
  padding: 4px 0;
}

.subitem-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(var(--item-color-rgb), 0.08);
  }

  &.is-active {
    background: rgba(var(--item-color-rgb), 0.12);

    .subitem-pip {
      box-shadow: 0 0 8px var(--item-color);
    }

    .subitem-label {
      color: rgba(255, 255, 255, 0.95);

      .theme--light & {
        color: rgba(0, 0, 0, 0.9);
      }
    }
  }
}

// ============================================================================
// Color Indicator Pip
// ============================================================================
.subitem-pip {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--item-color);
  flex-shrink: 0;
  transition: box-shadow 0.2s ease;

  .subitem-row:not(.is-active) & {
    opacity: 0.5;
  }
}

// ============================================================================
// Icon & Label
// ============================================================================
.subitem-icon {
  color: var(--item-color) !important;
  opacity: 0.7;
  flex-shrink: 0;

  .subitem-row.is-active & {
    opacity: 1;
  }
}

.subitem-label {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  transition: color 0.15s ease;

  .theme--light & {
    color: rgba(0, 0, 0, 0.65);
  }
}

// ============================================================================
// Check Mark
// ============================================================================
.subitem-check {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .v-icon {
    color: var(--item-color) !important;
  }
}

// ============================================================================
// Transitions
// ============================================================================
.subitems-overlay-enter-active {
  animation: subitemsIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.subitems-overlay-leave-active {
  animation: subitemsOut 0.12s ease-out;
}

@keyframes subitemsIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes subitemsOut {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px) scale(0.98);
  }
}
</style>
